import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Loader } from '@googlemaps/js-api-loader';

const LocationBoundaryEditor = ({ areas, currentArea: initialArea, locations: initialLocations, googleMapsApiKey }) => {
  const [map, setMap] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState(initialLocations);
  const [drawing, setDrawing] = useState({ locationId: null, vertexCount: 0 });
  const [showOffers, setShowOffers] = useState(true);
  const [offerMarkers, setOfferMarkers] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editablePolygon, setEditablePolygon] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [showOverlaps, setShowOverlaps] = useState(false);
  const [currentArea, setCurrentArea] = useState(initialArea);
  const [showPolygons, setShowPolygons] = useState(true);
  const [hiddenLocations, setHiddenLocations] = useState([]);

  const mapRef = useRef(null);
  const editingLocationRef = useRef(null);
  const mapInstanceRef = useRef(null);
  // Active click-to-draw session. Google decommissioned the Drawing Library in
  // Maps JS 3.65, so boundaries are drawn with plain map click listeners.
  const drawingRef = useRef(null);
  const labelOverlaysRef = useRef([]);
  const locationsRef = useRef(initialLocations);

  useEffect(() => {
    locationsRef.current = locations;
  }, [locations]);

  useEffect(() => {
    if (!mapRef.current) return;

    (async () => {
      try {
      // Ensure Image constructor exists (polyfill for any remaining issues)
      if (typeof window.Image !== 'function') {
        window.Image = class Image {
          constructor(width, height) {
            const img = document.createElement('img');
            if (width) img.width = width;
            if (height) img.height = height;
            return img;
          }
        };
      }

      // Load Maps asynchronously using the official loader
      const loader = new Loader({
        apiKey: googleMapsApiKey,
        version: 'weekly',
        libraries: ['geometry'],
      });

      const google = await loader.load();

      const { Map } = await google.maps.importLibrary('maps');

      // Use current area if present; otherwise Raleigh
      const mapCenter = currentArea.latitude && currentArea.longitude
        ? { lat: currentArea.latitude, lng: currentArea.longitude }
        : { lat: 35.7796, lng: -78.6382 };

      const newMap = new Map(mapRef.current, {
        center: mapCenter,
        zoom: 11,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'all',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'transit',
            elementType: 'all',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ visibility: 'on' }, { lightness: 40 }]
          },
          {
            featureType: 'landscape',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      setMap(newMap);
      mapInstanceRef.current = newMap; // Store map instance in ref

      // Style the data layer for polygons
      newMap.data.setStyle((feature) => {
        const color = feature.getProperty('color') || '#FF0000';
        return {
          fillColor: color,
          fillOpacity: 0.35,
          strokeColor: color,
          strokeWeight: 3,
          strokeOpacity: 1
        };
      });

      // Add click listener to data layer. Clicks on a data feature are consumed
      // by the feature and never bubble to the map, so while a drawing session
      // is open they have to be forwarded to the vertex handler — otherwise you
      // could not draw on top of an existing boundary.
      newMap.data.addListener('click', (event) => {
        if (drawingRef.current) {
          addVertex(event.latLng);
          return;
        }
        const locationId = event.feature.getProperty('id');
        const locationName = event.feature.getProperty('name');
        console.log(`Clicked on ${locationName}`);
        selectLocation(locationId);
      });

      newMap.data.addListener('dblclick', () => {
        if (drawingRef.current) finishDrawing();
      });

      newMap.data.addListener('rightclick', () => {
        if (drawingRef.current) undoVertex();
      });
      } catch (error) {
        console.error('Failed to initialize the boundary map:', error);
        setMapError(error.message || String(error));
      }
    })();
  }, []);

  useEffect(() => {
    if (!map) return;
    console.log('Rendering polygons for locations:', locations.filter(l => l.boundary_polygon).map(l => ({
      name: l.name,
      polygon_points: l.boundary_polygon ? l.boundary_polygon.length : 0,
      first_point: l.boundary_polygon ? l.boundary_polygon[0] : null,
      last_point: l.boundary_polygon ? l.boundary_polygon[l.boundary_polygon.length - 1] : null
    })));
    renderPolygons();
  }, [locations, map]);

  useEffect(() => {
    if (!map) return;
    renderOfferMarkers();
  }, [showOffers, locations, map]);
  
  useEffect(() => {
    if (!map) return;
    renderPolygons();
  }, [showPolygons, locations, map]);

  const renderPolygons = () => {
    // Clear existing data layer features
    if (map && map.data) {
      map.data.forEach((feature) => {
        map.data.remove(feature);
      });
    }
    
    // Clear old polygon labels
    labelOverlaysRef.current.forEach(label => {
      if (label && label.setMap) {
        label.setMap(null);
      }
    });
    labelOverlaysRef.current = [];

    // Only render if showPolygons is true
    if (!showPolygons || !map) {
      return;
    }

    // Use Google Maps Data layer for GeoJSON
    locations.forEach(location => {
      if (location.boundary_polygon && location.boundary_polygon.length > 0) {
        console.log(`Processing ${location.name} with ${location.boundary_polygon.length} points`);
        
        // Create GeoJSON feature
        const geoJsonFeature = {
          type: "Feature",
          properties: {
            name: location.name,
            id: location.id,
            color: location.boundary_color || '#FF0000'
          },
          geometry: {
            type: "Polygon",
            coordinates: [
              location.boundary_polygon.map(coord => [coord[1], coord[0]]) // Convert [lat, lng] to [lng, lat] for GeoJSON
            ]
          }
        };
        
        // Add to data layer
        const feature = map.data.addGeoJson(geoJsonFeature);
        console.log(`Added GeoJSON feature for ${location.name}`);
        
        // Calculate the center of the polygon for label placement
        const bounds = new window.google.maps.LatLngBounds();
        location.boundary_polygon.forEach(coord => {
          bounds.extend(new window.google.maps.LatLng(coord[0], coord[1]));
        });
        const center = bounds.getCenter();
        
        // Create a custom label overlay
        const labelOverlay = new window.google.maps.OverlayView();
        labelOverlay.onAdd = function() {
          const div = document.createElement('div');
          div.style.position = 'absolute';
          div.style.fontSize = '14px';
          div.style.fontWeight = 'bold';
          div.style.color = location.boundary_color || '#FF0000';
          div.style.textShadow = '2px 2px 4px rgba(255,255,255,0.9), -2px -2px 4px rgba(255,255,255,0.9), 2px -2px 4px rgba(255,255,255,0.9), -2px 2px 4px rgba(255,255,255,0.9)';
          div.style.pointerEvents = 'none';
          div.style.whiteSpace = 'nowrap';
          div.textContent = location.name;
          this.div = div;
          const panes = this.getPanes();
          panes.overlayLayer.appendChild(div);
        };
        
        labelOverlay.draw = function() {
          const overlayProjection = this.getProjection();
          const position = overlayProjection.fromLatLngToDivPixel(center);
          const div = this.div;
          div.style.left = (position.x - (div.offsetWidth / 2)) + 'px';
          div.style.top = (position.y - (div.offsetHeight / 2)) + 'px';
        };
        
        labelOverlay.onRemove = function() {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        };
        
        labelOverlay.locationId = location.id;
        labelOverlay.setMap(map);
        labelOverlaysRef.current.push(labelOverlay);
      }
    });
  };

  // Re-applies per-location visibility after anything re-renders the data layer.
  // Declared after the renderPolygons effects so it runs once they have finished.
  useEffect(() => {
    const currentMap = mapInstanceRef.current;
    if (!currentMap) return;

    currentMap.data.forEach((feature) => {
      const locationId = feature.getProperty('id');
      // Leave the boundary being drawn alone — it carries a dimming override.
      if (drawingRef.current && drawingRef.current.locationId === locationId) return;

      if (hiddenLocations.includes(locationId)) {
        currentMap.data.overrideStyle(feature, { visible: false });
      } else {
        currentMap.data.revertStyle(feature);
      }
    });

    labelOverlaysRef.current.forEach((label) => {
      label.setMap(hiddenLocations.includes(label.locationId) ? null : currentMap);
    });
  }, [hiddenLocations, locations, map, showPolygons]);

  const renderOfferMarkers = async () => {
    // Clear existing markers
    offerMarkers.forEach(marker => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });

    if (!showOffers || !map) {
      setOfferMarkers([]);
      return;
    }

    const newMarkers = [];
    
    // Helper function to check if a point is in any polygon
    const isPointInPolygon = (lat, lng, polygonCoords) => {
      if (!polygonCoords || polygonCoords.length === 0) return false;
      
      let inside = false;
      const x = lng, y = lat;
      
      for (let i = 0, j = polygonCoords.length - 1; i < polygonCoords.length; j = i++) {
        const xi = polygonCoords[i][1], yi = polygonCoords[i][0];
        const xj = polygonCoords[j][1], yj = polygonCoords[j][0];
        
        const intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      
      return inside;
    };

    locations.forEach(location => {
      (location.offers || []).forEach(offer => {
        (offer.addresses || []).forEach(address => {
          if (address.latitude && address.longitude) {
            // Find all locations this offer is assigned to
            const assignedLocations = locations
              .filter(loc => loc.offers && loc.offers.some(o => o.id === offer.id));
            
            // Find which boundaries actually contain this offer
            const containingLocations = locations
              .filter(loc => loc.boundary_polygon && 
                isPointInPolygon(address.latitude, address.longitude, loc.boundary_polygon));
            
            // Check if offer is in a boundary it's not assigned to
            const isInWrongBoundary = containingLocations.some(containingLoc => 
              !assignedLocations.some(assignedLoc => assignedLoc.id === containingLoc.id)
            );
            
            // Check if offer is assigned to at least one boundary it's actually in
            const isInAtLeastOneAssignedBoundary = assignedLocations.some(assignedLoc =>
              containingLocations.some(containingLoc => containingLoc.id === assignedLoc.id)
            );
            
            // Show warning if in wrong boundary AND not in any correct boundary
            const showWarning = isInWrongBoundary && !isInAtLeastOneAssignedBoundary && containingLocations.length > 0;
            
            // Use standard Marker instead of AdvancedMarkerElement to avoid the warnings
            const marker = new window.google.maps.Marker({
              position: { lat: address.latitude, lng: address.longitude },
              map: map,
              icon: showWarning ? {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: '#FF0000',
                fillOpacity: 0.9,
                strokeColor: '#FFFF00',
                strokeWeight: 3
              } : {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: location.boundary_color || '#FF0000',
                fillOpacity: 0.9,
                strokeColor: '#FFFFFF',
                strokeWeight: 2
              },
              title: offer.business_name
            });

            marker.addListener('click', () => {
              const assignedLocationNames = assignedLocations
                .map(loc => loc.name)
                .sort();
              
              const containingLocationNames = containingLocations
                .map(loc => loc.name)
                .sort();
              
              const infoWindow = new window.google.maps.InfoWindow({
                content: `<div>
                  <strong>${offer.business_name}</strong><br>
                  ${address.display_name || ''}<br>
                  <a href="/offers/${offer.id}/edit" target="_blank" style="color: #2196F3; text-decoration: underline;">Edit Offer →</a><br>
                  <br>
                  ${showWarning ? '<strong style="color: red;">⚠️ LOCATION MISMATCH</strong><br><br>' : ''}
                  <strong>Assigned Locations:</strong><br>
                  ${assignedLocationNames.length > 0 ? assignedLocationNames.join('<br>') : '<em>No locations assigned</em>'}<br>
                  <br>
                  <strong>Actually In Boundaries:</strong><br>
                  ${containingLocationNames.length > 0 ? containingLocationNames.join('<br>') : '<em>Outside all boundaries</em>'}
                </div>`
              });
              infoWindow.open(map, marker);
            });

            newMarkers.push(marker);
          }
        });
      });
    });

    setOfferMarkers(newMarkers);
  };

  // Reads through refs so it stays correct when called from map listeners bound
  // at mount time, where the `map`/`locations` state values are still stale.
  const selectLocation = (locationId) => {
    setSelectedLocation(locationId);
    const currentMap = mapInstanceRef.current;
    const location = locationsRef.current.find(l => l.id === locationId);

    if (currentMap && location && location.boundary_polygon && location.boundary_polygon.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      location.boundary_polygon.forEach(coord => {
        bounds.extend({ lat: coord[0], lng: coord[1] });
      });
      currentMap.fitBounds(bounds);
    }
  };

  const zoomToFitAll = () => {
    const bounds = new window.google.maps.LatLngBounds();
    let hasPolygons = false;
    
    locations.forEach(location => {
      if (location.boundary_polygon && location.boundary_polygon.length > 0) {
        location.boundary_polygon.forEach(coord => {
          bounds.extend(new window.google.maps.LatLng(coord[0], coord[1]));
          hasPolygons = true;
        });
      }
    });
    
    if (hasPolygons) {
      map.fitBounds(bounds);
      // Add some padding
      setTimeout(() => {
        const currentZoom = map.getZoom();
        if (currentZoom > 10) {
          map.setZoom(currentZoom - 1);
        }
      }, 100);
    }
  };

  const startDrawing = (locationId) => {
    const currentMap = mapInstanceRef.current;
    if (!currentMap) {
      alert('The map is still loading. Give it a moment and try again.');
      return;
    }

    // Restart cleanly if another drawing session is already open
    if (drawingRef.current) cancelDrawing();

    // Store the location ID for when polygon is complete
    setEditingLocation(locationId);
    editingLocationRef.current = locationId; // Also store in ref to avoid closure issues

    // You can't redraw a boundary you can't see, and keeping it hidden would
    // fight with the dimming override below.
    setHiddenLocations(prev => prev.filter(id => id !== locationId));

    const location = locations.find(l => l.id === locationId);
    const color = location?.boundary_color || '#FF0000';

    // Don't hide existing polygon - let user see the old boundary while drawing new one
    // Just make it semi-transparent
    currentMap.data.forEach((feature) => {
      if (feature.getProperty('id') === locationId) {
        currentMap.data.overrideStyle(feature, {
          fillOpacity: 0.1,
          strokeOpacity: 0.3
        });
      }
    });

    const previewPolygon = new window.google.maps.Polygon({
      paths: [],
      strokeColor: color,
      strokeOpacity: 1,
      strokeWeight: 3,
      fillColor: color,
      fillOpacity: 0.3,
      clickable: false, // let clicks fall through to the map
      editable: false,
      zIndex: 2,
      map: currentMap
    });

    const keydownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelDrawing();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        finishDrawing();
      } else if (event.key && event.key.toLowerCase() === 'z' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        undoVertex();
      }
    };

    // Offer markers would otherwise swallow clicks meant for the map
    offerMarkers.forEach(marker => marker.setOptions({ clickable: false }));

    drawingRef.current = {
      locationId,
      color,
      vertices: [],
      polygon: previewPolygon,
      markers: [],
      listeners: [],
      suppressedMarkers: offerMarkers,
      previousCursor: currentMap.get('draggableCursor'),
      keydownHandler
    };

    currentMap.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });

    drawingRef.current.listeners.push(
      currentMap.addListener('click', (event) => addVertex(event.latLng)),
      currentMap.addListener('dblclick', () => finishDrawing()),
      currentMap.addListener('rightclick', () => undoVertex())
    );

    document.addEventListener('keydown', keydownHandler);

    setDrawing({ locationId, vertexCount: 0 });
  };

  const addVertex = (latLng) => {
    const session = drawingRef.current;
    if (!session || !latLng) return;

    const isFirst = session.vertices.length === 0;

    session.vertices.push(latLng);
    session.polygon.setPath(session.vertices);

    const marker = new window.google.maps.Marker({
      position: latLng,
      map: mapInstanceRef.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: isFirst ? 7 : 5,
        fillColor: '#FFFFFF',
        fillOpacity: 1,
        strokeColor: session.color,
        strokeWeight: 2
      },
      zIndex: 3,
      // Only the first vertex is clickable, so it can close the ring. The rest
      // stay transparent to clicks so they keep reaching the map.
      clickable: isFirst,
      cursor: isFirst ? 'pointer' : 'crosshair',
      title: isFirst ? 'Click to close this boundary' : undefined
    });

    if (isFirst) {
      marker.addListener('click', () => finishDrawing());
    }

    session.markers.push(marker);
    setDrawing({ locationId: session.locationId, vertexCount: session.vertices.length });
  };

  const undoVertex = () => {
    const session = drawingRef.current;
    if (!session || session.vertices.length === 0) return;

    session.vertices.pop();
    const marker = session.markers.pop();
    if (marker) marker.setMap(null);
    session.polygon.setPath(session.vertices);

    setDrawing({ locationId: session.locationId, vertexCount: session.vertices.length });
  };

  // Tears down the on-map drawing affordances without touching boundary state.
  const teardownDrawing = () => {
    const session = drawingRef.current;
    if (!session) return;

    session.listeners.forEach(listener => listener.remove());
    session.markers.forEach(marker => marker.setMap(null));
    session.polygon.setMap(null);
    session.suppressedMarkers.forEach(marker => marker.setOptions({ clickable: true }));
    document.removeEventListener('keydown', session.keydownHandler);

    const currentMap = mapInstanceRef.current;
    if (currentMap) {
      currentMap.setOptions({
        draggableCursor: session.previousCursor || null,
        disableDoubleClickZoom: false
      });
    }

    drawingRef.current = null;
    setDrawing({ locationId: null, vertexCount: 0 });
  };

  const cancelDrawing = () => {
    const session = drawingRef.current;
    if (!session) return;

    const { locationId } = session;
    teardownDrawing();
    restoreBoundaryStyle(locationId);

    setEditingLocation(null);
    editingLocationRef.current = null;
  };

  const restoreBoundaryStyle = (locationId) => {
    const currentMap = mapInstanceRef.current;
    if (!currentMap) return;

    currentMap.data.forEach((feature) => {
      if (feature.getProperty('id') === locationId) {
        currentMap.data.revertStyle(feature);
      }
    });
  };

  const finishDrawing = () => {
    const session = drawingRef.current;
    if (!session) return;

    // A double-click fires two click events first, so the last two vertices land
    // on nearly the same spot — collapse consecutive duplicates before checking.
    const coordinates = [];
    session.vertices.forEach((vertex) => {
      const point = [vertex.lat(), vertex.lng()];
      const previous = coordinates[coordinates.length - 1];
      const isDuplicate = previous &&
        Math.abs(previous[0] - point[0]) < 1e-7 &&
        Math.abs(previous[1] - point[1]) < 1e-7;
      if (!isDuplicate) coordinates.push(point);
    });

    if (coordinates.length < 3) {
      alert('A boundary needs at least 3 points. Keep clicking on the map, or press Esc to cancel.');
      return;
    }

    const { locationId } = session;
    teardownDrawing();

    // Ensure polygon is closed (first and last points must be the same for GeoJSON)
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coordinates.push([...first]);
    }

    // Store in unsaved changes
    setUnsavedChanges(prev => ({
      ...prev,
      [locationId]: coordinates
    }));

    // Update local state to show the new boundary
    setLocations(prev => prev.map(loc =>
      loc.id === locationId
        ? { ...loc, boundary_polygon: coordinates }
        : loc
    ));

    restoreBoundaryStyle(locationId);
    setEditingLocation(null);
    editingLocationRef.current = null;
  };

  const startEditingPolygon = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    if (!location || !location.boundary_polygon) return;
    
    setEditingLocation(locationId);
    editingLocationRef.current = locationId;
    
    // Hide the data layer polygon temporarily
    map.data.forEach((feature) => {
      if (feature.getProperty('id') === locationId) {
        map.data.overrideStyle(feature, { visible: false });
      }
    });
    
    // Create an editable polygon
    const paths = location.boundary_polygon.map(coord => ({
      lat: coord[0],
      lng: coord[1]
    }));
    
    const polygon = new window.google.maps.Polygon({
      paths: paths,
      strokeColor: location.boundary_color || '#FF0000',
      strokeOpacity: 1,
      strokeWeight: 3,
      fillColor: location.boundary_color || '#FF0000',
      fillOpacity: 0.35,
      editable: true,
      draggable: false,
      map: map
    });
    
      // Listen for changes
    window.google.maps.event.addListener(polygon.getPath(), 'set_at', () => {
      console.log('Polygon vertex moved');
    });
    
    window.google.maps.event.addListener(polygon.getPath(), 'insert_at', () => {
      console.log('Polygon vertex added');
    });
    
    window.google.maps.event.addListener(polygon.getPath(), 'remove_at', () => {
      console.log('Polygon vertex removed');
    });
    
    setEditablePolygon(polygon);
  };

  const handlePolygonEdit = (locationId, polygon) => {
    const path = polygon.getPath();
    const coordinates = [];
    
    for (let i = 0; i < path.getLength(); i++) {
      const latLng = path.getAt(i);
      coordinates.push([latLng.lat(), latLng.lng()]);
    }
    
    // Ensure polygon is closed (first and last points must be the same for GeoJSON)
    if (coordinates.length > 0 && 
        (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
         coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
      coordinates.push([...coordinates[0]]);
    }
    
    setUnsavedChanges(prev => ({
      ...prev,
      [locationId]: coordinates
    }));
  };

  const saveLocationBoundary = async (locationId, coordinates) => {
    const location = locations.find(l => l.id === locationId);
    if (!location) {
      console.error('Location not found:', locationId);
      return;
    }

    try {
      const response = await fetch(`/admin/location_boundaries/${locationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          boundary_coordinates: JSON.stringify(coordinates),
          boundary_color: location.boundary_color || '#FF0000'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update local state with the saved boundary
        setLocations(prev => prev.map(loc => 
          loc.id === locationId 
            ? { ...loc, boundary_polygon: coordinates }
            : loc
        ));
        
        // Clear from unsaved changes if it exists
        setUnsavedChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[locationId];
          return newChanges;
        });
        
        // Show the data layer polygon again
        if (mapInstanceRef.current) {
          mapInstanceRef.current.data.forEach((feature) => {
            if (feature.getProperty('id') === locationId) {
              mapInstanceRef.current.data.revertStyle(feature);
            }
          });
        }
        
        alert(`✅ Saved ${location.name}! ${data.affected_offers ? data.affected_offers.length : 0} offers in this location.`);
      } else {
        alert('Error saving: ' + (data.errors || ['Unknown error']).join(', '));
      }
    } catch (error) {
      alert('Error saving location boundary: ' + error.message);
    }
  };


  const analyzeOverlaps = async () => {
    try {
      const response = await fetch(`/admin/location_boundaries/analyze_overlaps?area_id=${currentArea.id}`);
      const data = await response.json();
      
      if (data.overlaps && data.overlaps.length > 0) {
        console.log('Overlaps found:', data.overlaps);
        alert(`Found ${data.overlaps.length} overlapping location pairs. Check console for details.`);
        setShowOverlaps(true);
      } else {
        alert('No overlapping locations found!');
      }
    } catch (error) {
      alert('Error analyzing overlaps: ' + error.message);
    }
  };

  const toggleLocationVisibility = (locationId) => {
    setHiddenLocations(prev => (
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    ));
  };

  const changeLocationColor = (locationId, color) => {
    setLocations(prev => prev.map(loc =>
      loc.id === locationId ? { ...loc, boundary_color: color } : loc
    ));
  };

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Sidebar */}
      <div style={{ width: '300px', background: '#f5f5f5', padding: '15px', overflowY: 'auto' }}>
        <h2>Location Boundaries</h2>
        
        <div style={{ marginBottom: '15px' }}>
          <label>Area: </label>
          <select 
            value={currentArea.id} 
            onChange={(e) => window.location.href = `/admin/location_boundaries?area_id=${e.target.value}`}
            style={{ width: '100%', padding: '5px' }}
          >
            {areas.map(area => (
              <option key={area.id} value={area.id}>{area.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <button 
            onClick={() => setShowPolygons(!showPolygons)}
            style={{ 
              marginRight: '10px', 
              padding: '5px 10px',
              background: showPolygons ? '#2196F3' : '#ccc',
              color: 'white',
              border: 'none'
            }}
          >
            {showPolygons ? 'Hide' : 'Show'} Boundaries
          </button>
          <button 
            onClick={() => setShowOffers(!showOffers)}
            style={{ 
              marginRight: '10px', 
              padding: '5px 10px',
              background: showOffers ? '#2196F3' : '#ccc',
              color: 'white',
              border: 'none'
            }}
          >
            {showOffers ? 'Hide' : 'Show'} Offers
          </button>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <button 
            onClick={zoomToFitAll}
            style={{ marginRight: '10px', padding: '5px 10px', background: '#FF9800', color: 'white', border: 'none' }}
          >
            Fit All Boundaries
          </button>
          <button 
            onClick={analyzeOverlaps}
            style={{ marginRight: '10px', padding: '5px 10px' }}
          >
            Check Overlaps
          </button>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <button 
            onClick={() => window.location.href = '/admin/geojson_imports/new'}
            style={{ padding: '5px 10px', background: '#4CAF50', color: 'white', border: 'none' }}
          >
            Import GeoJSON
          </button>
        </div>

        {/* Removed bulk save section since we now save immediately per location */}

        <h3>Locations</h3>
        {locations.sort((a, b) => a.name.localeCompare(b.name)).map(location => (
          <div 
            key={location.id} 
            style={{ 
              marginBottom: '10px', 
              padding: '10px', 
              background: selectedLocation === location.id ? '#e0e0e0' : 'white',
              border: '1px solid #ddd',
              cursor: 'pointer'
            }}
            onClick={() => selectLocation(location.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{location.name}</strong>
              <input 
                type="color" 
                value={location.boundary_color || '#FF0000'}
                onChange={(e) => changeLocationColor(location.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '30px', height: '30px' }}
              />
            </div>
            
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {location.offers ? location.offers.length : 0} offers
              {location.boundary_polygon && ` • Boundary defined`}
            </div>
            
            <div style={{ marginTop: '5px' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (drawing.locationId === location.id) {
                    cancelDrawing();
                  } else {
                    startDrawing(location.id);
                  }
                }}
                style={{ marginRight: '5px', padding: '2px 8px', fontSize: '12px' }}
              >
                {drawing.locationId === location.id
                  ? 'Cancel Drawing'
                  : `${location.boundary_polygon ? 'Redraw' : 'Draw'} Boundary`}
              </button>
              
              {location.boundary_polygon && (
                <>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editingLocation === location.id) {
                        // Stop editing and mark as unsaved
                        if (editablePolygon) {
                          const path = editablePolygon.getPath();
                          const coordinates = [];
                          for (let i = 0; i < path.getLength(); i++) {
                            const latLng = path.getAt(i);
                            coordinates.push([latLng.lat(), latLng.lng()]);
                          }
                          
                          // Ensure polygon is closed
                          if (coordinates.length > 0 && 
                              (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
                               coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
                            coordinates.push([...coordinates[0]]);
                          }
                          
                          // Store in unsaved changes
                          setUnsavedChanges(prev => ({
                            ...prev,
                            [location.id]: coordinates
                          }));
                          
                          // Update local state
                          setLocations(prev => prev.map(loc => 
                            loc.id === location.id 
                              ? { ...loc, boundary_polygon: coordinates }
                              : loc
                          ));
                          
                          editablePolygon.setMap(null);
                          setEditablePolygon(null);
                        }
                        // Re-show the data layer polygon
                        if (mapInstanceRef.current) {
                          mapInstanceRef.current.data.forEach((feature) => {
                            if (feature.getProperty('id') === location.id) {
                              mapInstanceRef.current.data.revertStyle(feature);
                            }
                          });
                        }
                        setEditingLocation(null);
                      } else {
                        // Start editing
                        startEditingPolygon(location.id);
                      }
                    }}
                    style={{ marginRight: '5px', padding: '2px 8px', fontSize: '12px' }}
                  >
                    {editingLocation === location.id ? 'Stop Editing' : 'Edit'}
                  </button>
                  
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLocationVisibility(location.id);
                    }}
                    style={{ marginRight: '5px', padding: '2px 8px', fontSize: '12px' }}
                  >
                    {hiddenLocations.includes(location.id) ? 'Show' : 'Hide'}
                  </button>
                </>
              )}
              
              {unsavedChanges[location.id] && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const coordinates = unsavedChanges[location.id];
                    saveLocationBoundary(location.id, coordinates);
                  }}
                  style={{ 
                    padding: '2px 8px', 
                    fontSize: '12px', 
                    background: '#4CAF50', 
                    color: 'white', 
                    border: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  💾 Save
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}></div>

        {mapError && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '10px 14px',
            fontSize: '13px',
            color: '#b91c1c',
            zIndex: 5
          }}>
            The map failed to load: {mapError}
          </div>
        )}

        {drawing.locationId && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'rgba(255,255,255,0.97)',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '10px 14px',
            fontSize: '13px',
            zIndex: 5
          }}>
            <span>
              <strong>Drawing {locations.find(l => l.id === drawing.locationId)?.name}</strong>
              {' '}— click the map to add points ({drawing.vertexCount} so far).
              {' '}Finish by double-clicking, pressing Enter, or clicking the first point.
              {' '}Right-click undoes, Esc cancels.
            </span>
            <button
              onClick={finishDrawing}
              disabled={drawing.vertexCount < 3}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '6px',
                border: 'none',
                background: drawing.vertexCount < 3 ? '#e5e7eb' : '#3b82f6',
                color: drawing.vertexCount < 3 ? '#9ca3af' : '#fff',
                cursor: drawing.vertexCount < 3 ? 'default' : 'pointer'
              }}
            >
              Finish
            </button>
            <button
              onClick={undoVertex}
              disabled={drawing.vertexCount === 0}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: drawing.vertexCount === 0 ? '#9ca3af' : '#374151',
                cursor: drawing.vertexCount === 0 ? 'default' : 'pointer'
              }}
            >
              Undo
            </button>
            <button
              onClick={cancelDrawing}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Initialize the component when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('location-boundary-editor');
  if (container) {
    const areas = JSON.parse(container.dataset.areas);
    const currentArea = JSON.parse(container.dataset.currentArea);
    const locations = JSON.parse(container.dataset.locations);
    const googleMapsApiKey = container.dataset.googleMapsApiKey;
    
    ReactDOM.render(
      <LocationBoundaryEditor 
        areas={areas}
        currentArea={currentArea}
        locations={locations}
        googleMapsApiKey={googleMapsApiKey}
      />,
      container
    );
  }
});

export default LocationBoundaryEditor;